import { ConversationEmptyState } from '@crowdex/ui/ai/conversation';
import { Button } from '@crowdex/ui/components/button';
import { Bot, LoaderCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { NeedsYouCard } from '@/features/needs-you/needs-you-card';
import { useNeedsYou } from '@/features/needs-you/use-needs-you';
import type { SidebarThread } from '@/lib/sidebar-threads';

/** Where the cards of a new conversation come from, and how one opens its conversation. */
export type NewConversationNeedsYou = {
  threads: readonly SidebarThread[];
  preview: boolean;
  onOpen: (thread: SidebarThread) => void;
};

/**
 * A new conversation (at launch and ⌘N; there is no Home page): "What should we work on?",
 * the composer, and under it what waits on the user in every workspace, answered in place,
 * until something is typed. A phone shows the composer first, without the title.
 */
export function NewConversationStart({
  composer,
  typed,
  needsYou,
  phone = false,
}: {
  composer: ReactNode;
  /** The composer has text: the cards make way for the task being written. */
  typed: boolean;
  needsYou: NewConversationNeedsYou | null;
  phone?: boolean;
}) {
  return (
    <div className={phone ? 'flex flex-col gap-5' : 'flex flex-col gap-6'}>
      {!phone && (
        <h1 className="type-page-title text-center tracking-[-0.02em]">What should we work on?</h1>
      )}
      {composer}
      {needsYou !== null && !typed && <NeedsYouCards needsYou={needsYou} phone={phone} />}
    </div>
  );
}

/** The new conversation's "Needs you": every workspace's questions and approvals. */
function NeedsYouCards({ needsYou, phone }: { needsYou: NewConversationNeedsYou; phone: boolean }) {
  const { entries, answer } = useNeedsYou({
    threads: needsYou.threads,
    preview: needsYou.preview,
  });
  if (entries.length === 0) return null;
  return (
    <section aria-label="Needs you" className="flex flex-col gap-3">
      <h2 className="type-section font-semibold text-attention">Needs you · {entries.length}</h2>
      <div className={phone ? 'flex flex-col gap-3' : 'grid grid-cols-1 gap-3 md:grid-cols-2'}>
        {entries.map((entry) => (
          <NeedsYouCard
            entry={entry}
            key={entry.thread.id}
            onAnswer={(given) => answer(entry, given)}
            onOpen={() => needsYou.onOpen(entry.thread)}
            variant={phone ? 'mobile' : 'desktop'}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Before a conversation can start: no agent found yet, or none at all. Once there is one, the
 * pane shows {@link NewConversationStart} (its repository chip picks the folder).
 */
export function DesktopChatEmptyState({
  agentName,
  discoveringAgents,
  onRefreshAgents,
  runtime,
  syncFailed = false,
}: {
  agentName: string | null;
  discoveringAgents: boolean;
  onRefreshAgents: () => void;
  runtime?: 'cloud' | 'local';
  /** The conversations couldn't sync: their banner is the one message (M1), the agent is moot. */
  syncFailed?: boolean;
}) {
  if (agentName !== null || syncFailed) return null;
  const cloud = runtime === 'cloud';
  return (
    <ConversationEmptyState className="desktop-empty-state justify-[42%] gap-5">
      <div className="flex size-11 items-center justify-center rounded-xl border border-hairline-strong bg-card shadow-desktop-soft">
        {discoveringAgents ? (
          <LoaderCircle className="size-[18px] animate-spin text-muted-foreground" />
        ) : (
          <Bot className="size-[18px] text-muted-foreground" />
        )}
      </div>
      <div className="max-w-sm space-y-1.5 text-center">
        <h1 className="type-card-title">
          {discoveringAgents
            ? cloud
              ? 'Connecting to the cloud agent…'
              : 'Looking for local agents…'
            : cloud
              ? 'Cloud agent unavailable'
              : 'No ACP agent detected'}
        </h1>
        <p className="type-ui text-muted-foreground">
          {discoveringAgents
            ? cloud
              ? 'Preparing the cloud sandbox…'
              : 'Crowdex is checking the agents already installed on this machine.'
            : cloud
              ? 'The remote runtime is not configured or could not be reached.'
              : 'Install an ACP-compatible agent, then try the detection again.'}
        </p>
      </div>
      {!discoveringAgents && !cloud && (
        <Button onClick={onRefreshAgents} size="sm" variant="outline">
          Detect agents again
        </Button>
      )}
    </ConversationEmptyState>
  );
}
